import { HistoricalUtrs, DashboardItemConfig, SurveyUnitConfig } from '@g17eco/types/insight-custom-dashboard';
import { collectTableInitiatives, getActualUtrsData } from './utils';
import { sortEffectiveDateAsc } from '@utils/sort';
import './TableWidget.scss';
import { ColumnDef } from '@tanstack/react-table';
import { Table } from '@g17eco/molecules/table';
import { InputColumn } from '@g17eco/types/questionInterfaces';
import { getTableConfiguration } from '@utils/universalTracker';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { UniversalTrackerModalServiceUtrv, UtrvInitiativeRef } from '@g17eco/types/universalTrackerValue';
import { truncate } from '@utils/string';
import { useContext } from 'react';
import { CustomDashboardContext } from '@routes/custom-dashboard/context/CustomDashboardWrapper';
import { ValueListPlain } from '@g17eco/types/valueList';
import { getFormattedValueByColumn } from '@utils/utrvData';

const LIMIT_TRUNCATE_LETTERS = 40;

type DirectTableData = {
  utrv: UniversalTrackerModalServiceUtrv;
  data: {
    [columnCode: string]: string | number | string[] | undefined;
  };
};

export interface DirectTableParams {
  utrsData: HistoricalUtrs[];
  valueListMap: Map<string, ValueListPlain>;
  surveyUnitConfigMap: Map<string, SurveyUnitConfig>;
}

interface GetTableDataArgs {
  utrData: HistoricalUtrs | undefined;
  valueListMap: Map<string, ValueListPlain>;
  surveyUnitConfigMap: Map<string, SurveyUnitConfig>;
  redirectUtrvSource: (utrv: UniversalTrackerModalServiceUtrv) => void;
  wrapText: boolean;
  initiativeFilter?: string;
}

const getTableData = ({
  utrData,
  valueListMap,
  surveyUnitConfigMap,
  redirectUtrvSource,
  wrapText,
  initiativeFilter,
}: GetTableDataArgs) => {
  if (!utrData) {
    return { columns: [], data: [] };
  }

  const tableConfig = getTableConfiguration(utrData.utr);

  if (!tableConfig || !tableConfig.columns) {
    return { columns: [], data: [] };
  }

  const relevantUtrvs = initiativeFilter
    ? utrData.utrvs.filter((utrv) => utrv.initiative?._id === initiativeFilter)
    : utrData.utrvs;
  const sortedUtrvs = [...relevantUtrvs].sort(sortEffectiveDateAsc);

  const data: DirectTableData[] = sortedUtrvs
    .map((utrv) => {
      const tableRows: InputColumn[][] = utrv.valueData?.table ?? [];
      const surveyId = utrv.compositeData?.surveyId;
      const unitConfig = surveyId ? surveyUnitConfigMap.get(surveyId)?.unitConfig : undefined;

      return tableRows.map((row: InputColumn[]) => {
        const rowData: DirectTableData = {
          utrv,
          data: {},
        };

        row.forEach((cell) => {
          const column = tableConfig.columns.find((c) => c.code === cell.code);
          if (!column) {
            rowData.data[cell.code] = cell.value;
            return;
          }

          const formattedValueByColumn = getFormattedValueByColumn({
            data: row,
            column,
            unitConfig,
            valueListMap,
          });
          rowData.data[cell.code] = formattedValueByColumn;
        });

        return rowData;
      });
    })
    .flat();

  const columns: ColumnDef<DirectTableData>[] = tableConfig.columns.map((column) => ({
    id: column.code,
    header: () => (
      <SimpleTooltip text={column.shortName || column.name}>{column.shortName || column.name}</SimpleTooltip>
    ),
    meta: {
      headerProps: {
        className: 'text-truncate',
        style: {
          maxWidth: 250,
        },
      },
      cellProps: {
        className: 'text-center',
        style: { cursor: 'pointer' },
      },
    },
    cell: ({ row }) => {
      const value = row.original.data[column.code];
      if (value === undefined || value === null) {
        return '';
      }

      const handleCellClick = () => {
        if (row.original.utrv && redirectUtrvSource) {
          redirectUtrvSource(row.original.utrv);
        }
      };

      const displayValue = Array.isArray(value) ? value.join(', ') : String(value);

      if (wrapText) {
        return (
          <span className='d-inline-block' onClick={handleCellClick}>
            {displayValue}
          </span>
        );
      }

      const isTruncated = displayValue.length > LIMIT_TRUNCATE_LETTERS;
      const truncatedValue = truncate(displayValue, LIMIT_TRUNCATE_LETTERS);
      return (
        <SimpleTooltip text={isTruncated ? displayValue : undefined}>
          <span className='d-inline-block' onClick={handleCellClick}>
            {truncatedValue}
          </span>
        </SimpleTooltip>
      );
    },
    enableSorting: false,
  }));

  return { columns, data };
};

export interface DirectTableWidgetProps extends DirectTableParams {
  config?: DashboardItemConfig;
  itemId?: string;
}

interface SubsectionProps {
  utrData: HistoricalUtrs | undefined;
  valueListMap: Map<string, ValueListPlain>;
  surveyUnitConfigMap: Map<string, SurveyUnitConfig>;
  redirectUtrvSource: (utrv: UniversalTrackerModalServiceUtrv) => void;
  wrapText: boolean;
  initiative?: UtrvInitiativeRef;
}

const Subsection = ({
  utrData,
  valueListMap,
  surveyUnitConfigMap,
  redirectUtrvSource,
  wrapText,
  initiative,
}: SubsectionProps) => {
  const { columns, data } = getTableData({
    utrData,
    valueListMap,
    surveyUnitConfigMap,
    redirectUtrvSource,
    wrapText,
    initiativeFilter: initiative?._id,
  });

  return (
    <div
      data-initiative-id={initiative?._id}
      data-testid='direct-table-subsection'
      className={`table-widget__container direct-table-widget__container ${wrapText || data.length === 0 ? 'wrapped' : ''}`}
    >
      {initiative ? (
        <div className='text-sm text-ThemeTextLight mb-1' data-testid='direct-table-subsidiary-label'>
          {initiative.name}
        </div>
      ) : null}
      <Table columns={columns} data={data} pageSize={50} />
      {data.length === 0 ? (
        <p className='table-widget__no-data d-flex align-items-center justify-content-center text-muted bg-white m-0'>
          No data available
        </p>
      ) : null}
    </div>
  );
};

export const DirectTableWidget = (props: DirectTableWidgetProps) => {
  const { utrsData, valueListMap, surveyUnitConfigMap, config, itemId } = props;
  const { redirectUtrvSource = () => {} } = useContext(CustomDashboardContext);
  const wrapText = config?.wrapText ?? false;

  if (!utrsData.length) {
    return (
      <div
        data-table-item-id={itemId}
        data-testid='direct-table-empty'
        className='table-widget__container d-flex align-items-center justify-content-center text-ThemeTextLight'
      >
        No data available
      </div>
    );
  }

  const actualUtrsData = getActualUtrsData(utrsData);
  // DirectTable is single-UTR by design; `tableConfig` on this UTR drives the column schema.
  const utrData = actualUtrsData[0];
  const initiatives = collectTableInitiatives(actualUtrsData);
  const isDisaggregated = initiatives.length > 1;
  const subsections: (UtrvInitiativeRef | undefined)[] = isDisaggregated ? initiatives : [undefined];
  const wrapperProps = isDisaggregated
    ? { className: 'd-flex flex-column', style: { gap: '0.75rem' }, 'data-testid': 'direct-table-disaggregated' }
    : {};

  return (
    <div {...wrapperProps} data-table-item-id={itemId}>
      {subsections.map((initiative) => (
        <Subsection
          key={initiative?._id ?? 'aggregated'}
          utrData={utrData}
          valueListMap={valueListMap}
          surveyUnitConfigMap={surveyUnitConfigMap}
          redirectUtrvSource={redirectUtrvSource}
          wrapText={wrapText}
          initiative={initiative}
        />
      ))}
    </div>
  );
};
